import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBold, faItalic, faUnderline, faStrikethrough, faAlignLeft, faAlignCenter, faAlignRight, faAlignJustify, faListUl, faListOl, faIndent, faOutdent, faLink, faUnlink, faImage, faVideo, faCode, faEraser, faUndo, faRedo, faRefresh, faExpand, faGripVertical } from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import type { InlineToolbarCommand, InlineToolbarState } from "./inlineToolbarProtocol";

export interface InlineToolbarPlacement {
  manual?: { left: number; bottom: number };
  expandedHeight: number;
}

interface Props {
  anchor: RefObject<HTMLIFrameElement>;
  toolbarRef: RefObject<HTMLDivElement>;
  state: InlineToolbarState;
  disabled?: boolean;
  plainText?: boolean;
  hidden?: boolean;
  placementRef?: { current: InlineToolbarPlacement };
  activeAnchor?: { left: number; top: number; width: number; height: number };
  children?: ReactNode;
  onCommand: (command: InlineToolbarCommand, value?: string) => void;
  onRefreshMedia: () => void;
}

export function floatingToolbarPosition(anchor: DOMRect, width: number, height: number,
  bounds: { left: number; right: number; top: number; bottom: number }) {
  const left = Math.max(bounds.left + 8, Math.min(anchor.left, bounds.right - width - 8));
  const above = anchor.top - height - 8;
  const below = anchor.bottom + 8;
  const top = above >= bounds.top + 8 ? above
    : below + height <= bounds.bottom - 8 ? below : bounds.top + 8;
  return { left, top: Math.max(bounds.top + 8, Math.min(top, bounds.bottom - height - 8)) };
}

export default function FloatingInlineToolbar({ anchor, toolbarRef, state, disabled, plainText, hidden, placementRef, activeAnchor, children, onCommand, onRefreshMedia }: Props) {
  const [position, setPosition] = useState({ left: 16, top: 80, width: 760 });
  const ownPlacement = useRef<InlineToolbarPlacement>({ expandedHeight: 0 });
  const placement = placementRef ?? ownPlacement;
  const drag = useRef<{ pointer: number; x: number; y: number; left: number; top: number }>();
  const [manuallyPlaced, setManuallyPlaced] = useState(Boolean(placement.current.manual));
  const [placementRevision, setPlacementRevision] = useState(0);
  const clampManual = (left: number, top: number, width: number, height: number) => ({
    left: Math.max(8, Math.min(left, innerWidth - width - 8)),
    top: Math.max(8, Math.min(top, innerHeight - height - 8))
  });
  useLayoutEffect(() => {
    if (hidden) return;
    let frame = 0;
    const positionToolbar = () => {
      frame = 0;
      const element = anchor.current;
      const toolbar = toolbarRef.current;
      if (!element || !toolbar) return;
      let canvas = element.parentElement;
      while (canvas && !/(auto|scroll)/.test(getComputedStyle(canvas).overflowY)) canvas = canvas.parentElement;
      const canvasBounds = canvas?.getBoundingClientRect();
      const bounds = { left: Math.max(0, canvasBounds?.left ?? 0), right: Math.min(innerWidth, canvasBounds?.right ?? innerWidth),
        top: Math.max(0, canvasBounds?.top ?? 0) + 64, bottom: Math.min(innerHeight, canvasBounds?.bottom ?? innerHeight) };
      const width = Math.max(160, Math.min(760, bounds.right - bounds.left - 16));
      const frameBounds = element.getBoundingClientRect();
      const editingBounds = activeAnchor ? new DOMRect(frameBounds.left + activeAnchor.left, frameBounds.top + activeAnchor.top, activeAnchor.width, activeAnchor.height) : frameBounds;
      const height = toolbar.getBoundingClientRect().height;
      if (!plainText) placement.current.expandedHeight = height;
      // The bottom of the actions is the shared baseline. Formatting grows upward,
      // so entering Interact never moves the controls the user just clicked.
      const expandedHeight = Math.max(height, placement.current.expandedHeight);
      const automatic = floatingToolbarPosition(editingBounds, width, expandedHeight, bounds);
      const manual = placement.current.manual;
      const coordinates = manual
        ? clampManual(manual.left, manual.bottom - height, width, height)
        : { left: automatic.left, top: automatic.top + expandedHeight - height };
      if (manual) placement.current.manual = { left: coordinates.left, bottom: coordinates.top + height };
      const next = { ...coordinates, width };
      setPosition(previous => previous.left === next.left && previous.top === next.top && previous.width === next.width ? previous : next);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(positionToolbar); };
    const observer = new ResizeObserver(schedule);
    if (anchor.current) observer.observe(anchor.current);
    if (toolbarRef.current) observer.observe(toolbarRef.current);
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    positionToolbar();
    return () => { observer.disconnect(); cancelAnimationFrame(frame); window.removeEventListener("scroll", schedule, true); window.removeEventListener("resize", schedule); };
  }, [anchor, toolbarRef, activeAnchor, placementRevision, hidden, plainText, placement]);
  const unavailable = disabled || !state.ready;
  const button = (command: InlineToolbarCommand, title: string, icon: IconDefinition, active?: boolean, unavailableCommand = false) => (
    <button type="button" key={command} className={`inline-format-button${active ? " is-active" : ""}`} title={title} aria-label={title}
      aria-pressed={active} disabled={unavailable || unavailableCommand} onPointerDown={event => event.preventDefault()} onClick={() => onCommand(command)}>
      <FontAwesomeIcon icon={icon} />
    </button>
  );
  return createPortal(<div ref={toolbarRef} className="inline-editing-panel" role="group" aria-label="Inline text editing" style={{ ...position, display: hidden ? "none" : undefined }}
    onPointerDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}
    onKeyDown={event => { if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); onCommand("Focus"); } }}>
    <div className="inline-panel-heading">
      <div className="inline-panel-drag" role="button" tabIndex={0} aria-label="Move editing panel" title="Drag to move. Arrow keys move the panel; Escape returns to the text."
        onPointerDown={event => {
          if (event.button !== 0) return;
          event.preventDefault();
          const bounds = toolbarRef.current!.getBoundingClientRect();
          drag.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, left: bounds.left, top: bounds.top };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={event => {
          if (!drag.current || event.pointerId !== drag.current.pointer) return;
          const bounds = toolbarRef.current!.getBoundingClientRect();
          const next = clampManual(drag.current.left + event.clientX - drag.current.x,
            drag.current.top + event.clientY - drag.current.y, bounds.width, bounds.height);
          placement.current.manual = { left: next.left, bottom: next.top + bounds.height }; setManuallyPlaced(true);
          setPosition(previous => ({ ...previous, ...next }));
        }}
        onPointerUp={event => { if (drag.current?.pointer === event.pointerId) { drag.current = undefined; event.currentTarget.releasePointerCapture(event.pointerId); } }}
        onPointerCancel={() => { drag.current = undefined; }}
        onKeyDown={event => {
          const step = event.shiftKey ? 32 : 8;
          const directions: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
          if (!directions[event.key]) return;
          event.preventDefault(); event.stopPropagation();
          const [x, y] = directions[event.key];
          const bounds = toolbarRef.current!.getBoundingClientRect();
          const next = clampManual(bounds.left + x, bounds.top + y, bounds.width, bounds.height);
          placement.current.manual = { left: next.left, bottom: next.top + bounds.height }; setManuallyPlaced(true); setPosition(previous => ({ ...previous, ...next }));
        }}>
        <FontAwesomeIcon icon={faGripVertical} /> <span>Text editing</span>
      </div>
      <button type="button" className="inline-panel-auto" disabled={!manuallyPlaced} title="Return the panel next to this text field"
        onPointerDown={event => event.preventDefault()} onClick={() => { placement.current.manual = undefined; setManuallyPlaced(false); setPlacementRevision(value => value + 1); }}>Auto position</button>
    </div>
    {!plainText && <div className="inline-format-toolbar" role="toolbar" aria-label="Text formatting">
    <div className="inline-format-group">
      {button("Undo", "Undo", faUndo, undefined, !state.canUndo)}{button("Redo", "Redo", faRedo, undefined, !state.canRedo)}
    </div>
    <select aria-label="Text style" title="Text style" disabled={unavailable} value={state.format || "p"} onChange={event => onCommand("FormatBlock", event.target.value)}>
      {[["p", "Paragraph"], ["h1", "Heading 1"], ["h2", "Heading 2"], ["h3", "Heading 3"], ["h4", "Heading 4"], ["h5", "Heading 5"], ["h6", "Heading 6"], ["blockquote", "Quote"], ["pre", "Preformatted"]].map(([value, text]) => <option key={value} value={value}>{text}</option>)}
    </select>
    <select aria-label="Font size" title="Font size" disabled={unavailable} value={state.fontSize || ""} onChange={event => onCommand("FontSize", event.target.value)}>
      <option value="">Font size</option>{["0.5rem", "0.75rem", "1rem", "1.25rem", "1.5rem", "1.75rem", "2rem", "2.5rem", "3rem", "4rem", "5rem"].map(size => <option key={size} value={size}>{size}</option>)}
    </select>
    <select aria-label="Font family" title="Font family" disabled={unavailable} value={state.fontName || ""} onChange={event => onCommand("FontName", event.target.value)}>
      <option value="">Font</option>{["Arial", "Georgia", "Tahoma", "Times New Roman", "Trebuchet MS", "Verdana", "Courier New"].map(font => <option key={font} value={font}>{font}</option>)}
    </select>
    <div className="inline-format-group">
      {button("Bold", "Bold", faBold, state.bold)}{button("Italic", "Italic", faItalic, state.italic)}{button("Underline", "Underline", faUnderline, state.underline)}{button("Strikethrough", "Strikethrough", faStrikethrough, state.strikethrough)}
      <label title="Text colour" className="inline-format-colour">A<input aria-label="Text colour" type="color" disabled={unavailable} defaultValue="#000000" onChange={event => onCommand("ForeColor", event.target.value)} /></label>
      <label title="Highlight colour" className="inline-format-colour">▰<input aria-label="Highlight colour" type="color" disabled={unavailable} defaultValue="#ffff00" onChange={event => onCommand("HiliteColor", event.target.value)} /></label>
    </div>
    <div className="inline-format-group">
      {button("JustifyLeft", "Align left", faAlignLeft)}{button("JustifyCenter", "Align centre", faAlignCenter)}{button("JustifyRight", "Align right", faAlignRight)}{button("JustifyFull", "Justify", faAlignJustify)}
      {button("InsertUnorderedList", "Bullet list", faListUl, state.unorderedList)}{button("InsertOrderedList", "Numbered list", faListOl, state.orderedList)}
      {button("Outdent", "Decrease indent", faOutdent)}{button("Indent", "Increase indent", faIndent)}
    </div>
    <div className="inline-format-group">
      {button("mceLink", "Insert or edit link", faLink)}{button("Unlink", "Remove link", faUnlink)}{button("mceImage", "Insert or edit image", faImage)}{button("mceMedia", "Insert or edit media", faVideo)}
      {button("mceCodeEditor", "Edit HTML source", faCode)}{button("RemoveFormat", "Clear formatting", faEraser)}{button("mceFullScreen", "Fullscreen", faExpand)}
      <button type="button" className="inline-format-button" aria-label="Refresh site images" title="Refresh site images" disabled={unavailable} onPointerDown={event => event.preventDefault()} onClick={onRefreshMedia}><FontAwesomeIcon icon={faRefresh} /></button>
    </div>
    </div>}
    {children}
  </div>, document.body);
}
